import { writable } from 'svelte/store';

export const sidebarOpen = writable<boolean>(false);

// Mode switch confirmation dialog state
export const modeSwitchDialog = writable<{
  show: boolean;
  fromMode: string;
  toMode: string;
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
}>({ show: false, fromMode: '', toMode: '', onConfirm: null, onCancel: null });

/**
 * Whether "start a new selection" is waiting to be confirmed.
 *
 * # Why this asks now, when it did not before
 *
 * The button used to clear filters and results — things a customer can set
 * again in a few clicks — and StartOver argued that its red tint was warning
 * enough, in place of a dialog.
 *
 * Resetting now also deletes the customer's uploaded list, which is a file they
 * had to find and minutes of matching they had to wait for. That is no longer
 * something to lose to a misplaced click, so the tint got a dialog behind it.
 *
 * Only `show` is carried: unlike the mode switch there is nothing to remember
 * about which reset was asked for, because there is only one.
 */
export const resetDialog = writable<{ show: boolean }>({ show: false });

/**
 * Whether the export history has the screen.
 *
 * A flag rather than a route, like the saved list beside it: the members' app
 * draws its screens from state and has no URLs for them yet. See
 * docs/refactor/screens.md, which is where that is written down as the thing
 * to fix next.
 */
export const exportHistoryOpen = writable(false);

/**
 * How tall the summary bar currently is, in pixels.
 *
 * Anything floating above the working area has to clear that bar, and its
 * height is not a constant: below `sm` it stacks its figures over its button
 * instead of using the 64px grid, and the too-large notice adds a band on top
 * of that. Measured, it is 65px, 123px or 182px depending on width and whether
 * the notice is up — a 117px spread.
 *
 * The assistant's launcher and panel were each carrying a hardcoded guess at
 * it (`bottom-20 sm:bottom-24`, `bottom-16 sm:bottom-20`), which is two guesses
 * at one number, both wrong in some combination. PricingBar publishes the real
 * value here and they read it.
 *
 * Zero when no bar is on screen, which is what PricingBar resets it to when it
 * is destroyed — so a floating control falls back to sitting against the
 * window rather than above a bar that is not there.
 */
export const summaryBarHeight = writable(0);
